/**
 * Ceiling for one URL scan. The pattern is linear and each match is at most
 * 2048 characters, so a normal chat message or article — including links past
 * the old 10k cutoff — is scanned in full. Only an input this large is cut
 * off; the remainder stays plain text so a multi-megabyte paste cannot stall
 * the render thread.
 */
export const URL_MATCH_INPUT_LIMIT = 1_000_000;

/**
 * Linear URL matcher. Only http, https, ftp, and www hosts match — other
 * schemes such as javascript: are left as plain text so they cannot become an
 * href. The final character cannot be sentence punctuation, and backtracking
 * only walks back over that punctuation, so match time stays proportional to
 * the scanned length.
 */
const BOUNDED_URL_PATTERN = /(?:(?:https?|ftp):\/\/|www\.)[^\s<>"']{0,2047}[^\s<>"'.,;:!?)\]}]/gi;
const SAFE_LINK_SCHEME = /^(?:https?|ftp):\/\//i;

const boundedUrlRegex = new RegExp(BOUNDED_URL_PATTERN.source, 'gi');

export function createBoundedUrlRegex(): RegExp {
  return new RegExp(BOUNDED_URL_PATTERN.source, 'gi');
}

/** http(s) or ftp href, or `https://` for a www host. Null for any other scheme. */
export function toSafeLinkHref(url: string): string | null {
  if (SAFE_LINK_SCHEME.test(url)) {
    return url;
  }
  if (/^www\./i.test(url)) {
    return `https://${url}`;
  }
  return null;
}

export interface BoundedUrlMatch {
  end: number;
  start: number;
  url: string;
}

/**
 * URLs in `input`, without trailing sentence punctuation. A closing paren is
 * kept only when it balances an opening paren inside the URL.
 * Scans at most {@link URL_MATCH_INPUT_LIMIT} characters.
 */
export function matchBoundedUrls(input: string): BoundedUrlMatch[] {
  const { scanned } = splitUrlMatchInput(input);
  boundedUrlRegex.lastIndex = 0;
  const matches: BoundedUrlMatch[] = [];
  let match: RegExpExecArray | null;
  while ((match = boundedUrlRegex.exec(scanned))) {
    const absorbed = absorbBalancedClosingParens(scanned, match[0], boundedUrlRegex.lastIndex);
    boundedUrlRegex.lastIndex = absorbed.end;
    matches.push({ end: absorbed.end, start: match.index, url: absorbed.url });
  }
  return matches;
}

export function replaceBoundedUrls(input: string, replace: (url: string) => string): string {
  const { scanned, tail } = splitUrlMatchInput(input);
  const matches = matchBoundedUrls(scanned);
  if (matches.length === 0) {
    return input;
  }
  let result = '';
  let cursor = 0;
  for (const match of matches) {
    result += scanned.slice(cursor, match.start) + replace(match.url);
    cursor = match.end;
  }
  return result + scanned.slice(cursor) + tail;
}

function absorbBalancedClosingParens(input: string, url: string, end: number): { end: number; url: string } {
  let unmatched = 0;
  for (let i = 0; i < url.length; i++) {
    const char = url[i];
    if (char === '(') {
      unmatched++;
    } else if (char === ')' && unmatched > 0) {
      unmatched--;
    }
  }
  while (unmatched > 0 && input[end] === ')') {
    url += ')';
    end++;
    unmatched--;
  }
  return { end, url };
}

/**
 * Prefix the matcher may scan, plus any pathological remainder left as plain text.
 * Ordinary messages are shorter than {@link URL_MATCH_INPUT_LIMIT}, so `tail` is empty.
 */
export function splitUrlMatchInput(input: string): { scanned: string; tail: string } {
  if (input.length <= URL_MATCH_INPUT_LIMIT) {
    return { scanned: input, tail: '' };
  }
  return {
    scanned: input.slice(0, URL_MATCH_INPUT_LIMIT),
    tail: input.slice(URL_MATCH_INPUT_LIMIT),
  };
}

export const removeFalsyFromQueryParams = (queryString: string): string => {
  const params = new URLSearchParams(queryString);
  [...params.entries()].forEach(([key, value]) => {
    if (!value) {
      params.delete(key);
    }
  });
  const cleanedParams = String(params);
  return cleanedParams;
};

export const fixedEncodeURI = (uri: string) => {
  return encodeURI(uri).replace(/%5B/g, '[').replace(/%5D/g, ']');
};

export const validURL = (url: string) => {
  try {
    return Boolean(new URL(url));
  } catch (_e) {
    return false;
  }
};

export const convertToRelativeUrl = (url: string) => url?.replace('https://www.benzinga.com/', '/');

export const getYoutubeVideoIdFromUrl = (url: string) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

export const appendQueryParams = (url: string, params: Record<string, string | undefined>) => {
  const urlObj = new URL(url);
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      urlObj.searchParams.set(key, value);
    }
  });
  return urlObj.toString();
};

export function parseUtmSourceFromUrl(url: string | undefined): string | null {
  if (!url) return null;
  const queryStart = url.indexOf('?');
  if (queryStart === -1) return null;
  const params = new URLSearchParams(url.slice(queryStart));
  return params.get('utm_source');
}

export function queryToParams(query: Record<string, string | string[] | undefined>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(query).map(([key, value]) => [key, Array.isArray(value) ? (value[0] ?? '') : (value ?? '')]),
  );
}

export function getUtmSource(
  url: string | undefined,
  query?: Record<string, string | string[] | undefined>,
): string | null {
  const fromQuery = query?.utm_source;
  const utmSourceFromQuery = typeof fromQuery === 'string' ? fromQuery : null;
  return utmSourceFromQuery ?? parseUtmSourceFromUrl(url);
}
